import { cur } from '@/lib/currency';
import { useEffect, useMemo, useState } from 'react';
import { useBranchStore, useAuthStore } from '@/stores';
import { TbReconcilePanel } from '@/components/accounting/TbReconcilePanel';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ExportButtons } from '@/components/ExportButtons';
import { Scale, CheckCircle, XCircle, ArrowRight, Search } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { localToday } from '@/lib/dateRange';
import { PageSizeSelector } from '@/components/common/PageSizeSelector';
import { useLedgerDrilldown } from '@/components/accounting/LedgerDrilldown';
import { ReconciledBadge } from '@/components/accounting/ReconciledBadge';
import { ReportHeader } from '@/components/reports/ReportUI';
import { ReportFilterBar } from '@/components/reports/ReportFilterBar';
import { FilterEmptyNudge } from '@/components/common/FilterEmptyNudge';
import { ReportNotifyButton } from '@/components/accounting/ReportNotifyButton';
import { BranchCoverageNotice, forbiddenDetail } from '@/components/accounting/BranchCoverageNotice';

export default function TrialBalancePage() {
  const posBranch = useBranchStore((s) => s.activeBranchId);
  const role = useAuthStore((s) => s.user?.role);
  const [data, setData] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchSel, setBranchSel] = useState(posBranch ? [posBranch] : []);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState(localToday());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showZero, setShowZero] = useState(true);
  const [tbPageSize, setTbPageSize] = useState(50);
  const [tbPage, setTbPage] = useState(1);
  const { openLedger } = useLedgerDrilldown();

  const drillDown = (account) => {
    // Open the reusable ledger drill-down modal (stays on this page).
    openLedger(account.code || account.name, {
      name: account.name,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    });
  };

  const fetchData = async (o = {}) => {
    setLoading(true);
    try {
      const _end = o.endDate !== undefined ? o.endDate : endDate;
      const _start = o.startDate !== undefined ? o.startDate : startDate;
      const _bs = o.branchSel !== undefined ? o.branchSel : branchSel;
      const params = { end_date: _end };
      if (_start) params.start_date = _start;
      if (showZero) params.include_zero = 'true';
      if (_bs.length) params.branch_ids = _bs.join(',');
      const res = await api.get('/accounting/trial-balance', { params });
      setData(res.data); setBlocked(null);
    } catch (e) { const d = forbiddenDetail(e); if (d) { setBlocked(d); setData(null); } else toast.error('Failed to load trial balance'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [showZero, branchSel, startDate, endDate]);
  useEffect(() => { api.get('/branches').then(r => setBranches(r.data || [])).catch(() => {}); }, []);
  // Follow the shared POS/Stock active branch (branchStore).
  useEffect(() => { setBranchSel(posBranch ? [posBranch] : []); }, [posBranch]);

  // iter432 — Filter accounts by search term and zero-balance toggle.
  const filteredAccounts = useMemo(() => {
    const all = data?.accounts || [];
    const q = search.trim().toLowerCase();
    return all.filter(a => {
      if (!showZero && (!a.debit || a.debit === 0) && (!a.credit || a.credit === 0)) return false;
      if (!q) return true;
      return (a.code || '').toLowerCase().includes(q)
        || (a.name || '').toLowerCase().includes(q)
        || (a.type || '').toLowerCase().includes(q);
    });
  }, [data, search, showZero]);

  // iter966 — reset to page 1 whenever the filtered set or page size changes,
  // so a stale page number can't point past the end of a smaller result.
  useEffect(() => { setTbPage(1); }, [search, showZero, tbPageSize, data]);
  const tbTotalPages = Math.max(1, Math.ceil(filteredAccounts.length / tbPageSize));
  const pagedAccounts = filteredAccounts.slice((tbPage - 1) * tbPageSize, tbPage * tbPageSize);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="trial-balance-page">
        <ReportHeader icon={Scale} title={<span className="flex items-center gap-3">Trial Balance{data && <ReconciledBadge debit={data.total_debits} credit={data.total_credits} difference={data.difference} />}</span>}
          subtitle="Verify that total debits equal total credits" testId="tb-header"
          actions={<ReportNotifyButton report="trial_balance" label="Notify" />} />
        <ReportFilterBar testId="tb-filter" branches={branches} branchIds={branchSel} onBranchIds={setBranchSel}
          range={{ start: startDate || '…', end: endDate }} onRange={(r) => { setStartDate(r.start); setEndDate(r.end); }} defaultPreset="">
          <ExportButtons dataType="trial_balance" label="Trial Balance" startDate={startDate} endDate={endDate} branchIds={branchSel} />
        </ReportFilterBar>

        <BranchCoverageNotice unavailable={blocked} coverage={data?.branch_coverage} testid="tb-coverage" />
        {['admin', 'owner', 'super_admin'].includes(role) && !blocked && <TbReconcilePanel startDate={startDate} endDate={endDate} branchSel={branchSel} />}
        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Card className="border-info/30"><CardContent className="pt-5 text-center">
                <p className="text-[10px] text-info uppercase tracking-wider font-semibold">Total Debits</p>
                <p className="text-2xl font-bold text-info mt-1" data-testid="tb-total-debits">{cur()} {data.total_debits?.toLocaleString()}</p>
              </CardContent></Card>
              <Card className="border-error/30"><CardContent className="pt-5 text-center">
                <p className="text-[10px] text-error uppercase tracking-wider font-semibold">Total Credits</p>
                <p className="text-2xl font-bold text-error mt-1" data-testid="tb-total-credits">{cur()} {data.total_credits?.toLocaleString()}</p>
              </CardContent></Card>
              <Card className={`border-2 ${data.is_balanced ? 'border-success/30 bg-success/10' : 'border-error/30 bg-error/10'}`}>
                <CardContent className="pt-5 text-center">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Status</p>
                  <div className="flex items-center justify-center gap-2 mt-1" data-testid="tb-status">
                    {data.is_balanced ? <CheckCircle className="text-success" size={20} /> : <XCircle className="text-error" size={20} />}
                    <span className={`text-lg font-bold ${data.is_balanced ? 'text-success' : 'text-error'}`}>
                      {data.is_balanced ? 'Balanced' : `Unbalanced — difference: ${cur()} ${data.difference?.toLocaleString()}${data.sources?.artificial_adjustment ? ' (synthetic Suspense line included)' : ''}`}
                    </span>
                  </div>
                </CardContent>
              </Card>
              <Card><CardContent className="pt-5 text-center">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Accounts</p>
                <p className="text-2xl font-bold mt-1">{data.accounts?.length || 0}</p>
              </CardContent></Card>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30 flex-wrap" data-testid="tb-toolbar">
                  <div className="relative flex-1 min-w-[220px]">
                    <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Search code, name or type…"
                      className="h-8 pl-7 text-xs"
                      data-testid="tb-search"
                    />
                  </div>
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground select-none cursor-pointer" data-testid="tb-show-zero-label">
                    <input type="checkbox" checked={showZero} onChange={e => setShowZero(e.target.checked)} data-testid="tb-show-zero" />
                    Show zero-balance
                  </label>
                  <span className="text-[10px] text-muted-foreground" data-testid="tb-row-count">{filteredAccounts.length} of {data.accounts?.length || 0}</span>
                  <PageSizeSelector value={tbPageSize} onChange={setTbPageSize} testid="tb-page-size" />
                  {tbTotalPages > 1 && (
                    <div className="flex items-center gap-1" data-testid="tb-pagination">
                      <Button size="sm" variant="outline" disabled={tbPage <= 1} onClick={() => setTbPage(p => p - 1)}>Prev</Button>
                      <span className="text-[10px] text-muted-foreground">Page {tbPage} / {tbTotalPages}</span>
                      <Button size="sm" variant="outline" disabled={tbPage >= tbTotalPages} onClick={() => setTbPage(p => p + 1)}>Next</Button>
                    </div>
                  )}
                </div>
                <table className="w-full text-sm" data-testid="tb-table">
                  <thead><tr className="border-b bg-muted/50 text-muted-foreground">
                    <th className="text-left p-3 font-medium">Code</th>
                    <th className="text-left p-3 font-medium">Account Name</th>
                    <th className="text-center p-3 font-medium">Type</th>
                    <th className="text-right p-3 font-medium">Debit (SR)</th>
                    <th className="text-right p-3 font-medium">Credit (SR)</th>
                  </tr></thead>
                  <tbody>
                    {pagedAccounts.map((a, i) => (
                      <tr
                        key={i}
                        className="border-b hover:bg-primary/5 cursor-pointer transition-colors group"
                        onClick={() => drillDown(a)}
                        data-testid={`tb-row-${a.code || i}`}
                        title="Click to see all journal entries for this account"
                      >
                        <td className="p-3 font-mono text-xs text-muted-foreground group-hover:text-primary">{a.code}</td>
                        <td className="p-3 font-medium">
                          <div className="flex items-center gap-2">
                            <span>{a.name}</span>
                            <ArrowRight size={12} className="opacity-0 group-hover:opacity-60 transition-opacity text-primary" />
                          </div>
                        </td>
                        <td className="p-3 text-center"><Badge variant="outline" className="capitalize text-[10px]">{a.type}</Badge></td>
                        <td className="p-3 text-right font-mono text-info">{a.debit > 0 ? a.debit.toLocaleString() : '—'}</td>
                        <td className="p-3 text-right font-mono text-error">{a.credit > 0 ? a.credit.toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                    {filteredAccounts.length === 0 && (
                      <tr><td colSpan={5} className="p-0">
                        {search
                          ? <div className="text-center py-8 text-muted-foreground">No accounts match your search</div>
                          : <FilterEmptyNudge show onReset={() => { const td = localToday(); setStartDate(''); setEndDate(td); setBranchSel([]); fetchData({ startDate: '', endDate: td, branchSel: [] }); }} label="No data for the current filter" />}
                      </td></tr>
                    )}
                  </tbody>
                  {filteredAccounts.length > 0 && (
                    <tfoot><tr className="bg-muted font-bold border-t-2">
                      <td colSpan={3} className="p-3 text-right">Totals</td>
                      <td className="p-3 text-right font-mono text-info">{cur()} {data.total_debits?.toLocaleString()}</td>
                      <td className="p-3 text-right font-mono text-error">{cur()} {data.total_credits?.toLocaleString()}</td>
                    </tr></tfoot>
                  )}
                </table>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
